import { useMemo, useRef } from "react"

import { DEFAULT_PAGE_SIZE } from "@/constants/pagination"
import type { SortDirection } from "@/constants/sort"
import {
  useRunSimulationsKeysetTransport,
  useRunsKeysetTransport,
} from "@/runs"
import { normalizeRunStatus } from "@/runs/values"

import type { MetricValueFilter } from "@/lib/metricValueFilters"
import type { SortColumn } from "@/types_api/backend/transport/runs_keyset_request"
import type { RunsTableRowResource } from "@/types_api/backend/transport/runs_keyset_response"
import type { SimulationTableRowResource } from "@/types_api/backend/transport/simulated_conversations_keyset_response"
import type { MetadataFilter } from "@/types/MetadataFilter"
import type { RunsTableResult } from "@/types/tables/RunsTable"
import type { SimulationsTableResult } from "@/types/tables/SimulationsTable"

import { RUNS_VIEW_MODE, type RunsViewMode } from "../types/viewMode"
import { buildRunsTransportFilters } from "../utils/transportRunsFilters"

// Local type extension for `error_status` which the generated types do not
// yet include. Once the backend generated-types branch is updated, this
// local extension can be removed.
type RunsTableRowWithOptionalErrorStatus = RunsTableRowResource & {
  error_status?: string | null
}
type SimulationTableRowWithOptionalCallSid = SimulationTableRowResource & {
  call_sid?: string | null
}

export function normalizeSimulationTableRow(
  row: SimulationTableRowWithOptionalCallSid,
): SimulationsTableResult {
  return {
    ...row,
    call_sid: row.call_sid ?? null,
    created_at: row.created_at ?? "",
    created_by: row.created_by ?? "",
    created_by_user_id: row.created_by_user_id ?? null,
    metric_ids: row.metric_ids ?? [],
    metric_values: row.metric_values ?? undefined,
    model_type: row.model_type ?? "",
    persona_id_id: row.persona_id ?? null,
    status: normalizeRunStatus(row.status),
    tags: row.tags ?? [],
  }
}

export function normalizeRunTableRow(
  row: RunsTableRowWithOptionalErrorStatus,
): RunsTableResult {
  return {
    ...row,
    created_at: row.created_at ?? "",
    created_by: row.created_by ?? "",
    created_by_user_id: row.created_by_user_id ?? null,
    display_name: row.display_name ?? undefined,
    error_status: row.error_status ?? undefined,
    metric_averages: row.metric_averages ?? undefined,
    metric_ids: row.metric_ids ?? [],
    model_type: row.model_type ?? "",
    persona_id_id: row.persona_id ?? null,
    status: normalizeRunStatus(row.status),
    tags: row.tags ?? [],
    template_name: row.template_name ?? undefined,
  }
}

interface KeysetPageParam {
  cursor?: string | null
  limit?: number
}

export function getLoadedCursorsFromPageParams(
  pageParams: readonly (KeysetPageParam | undefined)[] | undefined,
): (string | undefined)[] {
  return pageParams?.map((pageParam) => pageParam?.cursor ?? undefined) ?? []
}

export interface RunsKeysetFilters {
  searchQuery?: string
  agentFilters?: string[]
  statusFilters?: string[]
  templateFilters?: string[]
  testSetFilters?: string[]
  testCaseFilters?: string[]
  personaFilters?: string[]
  metricFilters?: string[]
  metricValueFilters?: MetricValueFilter[]
  tagFilters?: string[]
  mutationFilters?: string[]
  scheduledRunId?: string
  metadataFilters?: MetadataFilter[]
  scheduledJobFilters?: string[]
  sortColumn?: SortColumn
  sortDirection?: SortDirection
  dateRange?: {
    start?: Date
    end?: Date
  }
}

/**
 * Hook for infinite scroll runs/simulations table data using keyset pagination.
 */
export function useRunsInfiniteQuery({
  viewMode,
  filters,
  enabled = true,
}: {
  viewMode: RunsViewMode
  filters: RunsKeysetFilters
  enabled?: boolean
}) {
  const transportFilters = useMemo(
    () => buildRunsTransportFilters(filters),
    [filters],
  )

  const keysetRequest = useMemo(
    () =>
      transportFilters
        ? {
            limit: DEFAULT_PAGE_SIZE,
            sort_column: filters.sortColumn,
            sort_direction: filters.sortDirection,
            filters: transportFilters,
          }
        : null,
    [filters.sortColumn, filters.sortDirection, transportFilters],
  )

  // Runs keyset query
  const runsQuery = useRunsKeysetTransport({
    request: keysetRequest,
    enabled: enabled && viewMode === RUNS_VIEW_MODE.RUNS,
  })

  // Simulations keyset query
  const simulationsQuery = useRunSimulationsKeysetTransport({
    request: keysetRequest,
    enabled: enabled && viewMode === RUNS_VIEW_MODE.SIMULATIONS,
  })

  const activeQuery =
    viewMode === RUNS_VIEW_MODE.RUNS ? runsQuery : simulationsQuery

  // Flatten and deduplicate runs
  const allRuns = useMemo((): RunsTableResult[] => {
    if (
      !keysetRequest ||
      viewMode !== RUNS_VIEW_MODE.RUNS ||
      !runsQuery.data?.pages
    ) {
      return []
    }
    const seen = new Set<string>()
    return runsQuery.data.pages
      .flatMap((page) => page.items)
      .map((row) => normalizeRunTableRow(row))
      .filter((row) => {
        if (seen.has(row.id)) {
          return false
        }
        seen.add(row.id)
        return true
      })
  }, [keysetRequest, runsQuery.data?.pages, viewMode])

  // Flatten and deduplicate simulations
  const allSimulations = useMemo((): SimulationsTableResult[] => {
    if (
      !keysetRequest ||
      viewMode !== RUNS_VIEW_MODE.SIMULATIONS ||
      !simulationsQuery.data?.pages
    ) {
      return []
    }
    const seen = new Set<string>()
    return simulationsQuery.data.pages
      .flatMap((page) => page.items)
      .map((row) => normalizeSimulationTableRow(row))
      .filter((row) => {
        if (seen.has(row.simulation_output_id)) {
          return false
        }
        seen.add(row.simulation_output_id)
        return true
      })
  }, [keysetRequest, viewMode, simulationsQuery.data?.pages])

  // Get loaded cursors for session state
  const loadedCursors = useMemo((): (string | undefined)[] => {
    // Transport infinite queries store the full request as each page param; the
    // runs table session state only needs the cursor boundary from each request.
    return getLoadedCursorsFromPageParams(activeQuery.data?.pageParams)
  }, [activeQuery.data?.pageParams])

  // Once any query settles (isFetched), keep that flag true forever for this
  // component instance. This prevents filter sub-menus from re-entering loading
  // state when the query key changes mid-init (e.g. metricRecordsById loads
  // after the first query fires and causes a new query key).
  const hasSettledRef = useRef(false)
  if (activeQuery.isFetched) {
    hasSettledRef.current = true
  }

  return {
    allRuns,
    allSimulations,
    loadedCursors,
    hasInitiallyLoaded: activeQuery.data !== undefined,
    hasSettledInitialFetch: hasSettledRef.current,

    hasNextPage: activeQuery.hasNextPage ?? false,
    fetchNextPage: activeQuery.fetchNextPage,
    isFetchingNextPage: activeQuery.isFetchingNextPage,
    isError: activeQuery.isError,
    error: activeQuery.error,
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching,
    isPlaceholderData: activeQuery.isPlaceholderData,
    refetch: activeQuery.refetch,
  }
}
